import { useEffect, useState } from "react";
import { Check, Loader2, Mail, Pencil, Trash2, User as UserIcon, UserPlus, X } from "lucide-react";

import { api } from "@/api/client";
import { useAuth } from "@/lib/AuthContext";

const MIN_PASSWORD = 8;

/**
 * Shop accounts.
 *
 * Sign-ups are switched off in Supabase, so everything here is the only way an
 * account comes into existence or changes. Two ways to add someone, because
 * the shop needs both: set a password and they can sign in immediately with
 * nothing emailed, or send an invitation and they choose their own.
 */
export default function UsersSection() {
  const { user: currentUser, reload } = useAuth();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const blankDraft = {
    email: "",
    full_name: "",
    role: "operator",
    method: "password",
    password: "",
  };
  const [draft, setDraft] = useState(blankDraft);
  const [adding, setAdding] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  /** The account being edited, as a working copy. */
  const [edit, setEdit] = useState(null);

  /** The account an administrator has asked to delete, pending confirmation. */
  const [removing, setRemoving] = useState(null);

  const [myName, setMyName] = useState(currentUser?.full_name ?? "");
  const [savingName, setSavingName] = useState(false);

  const load = async () => {
    try {
      setUsers(await api.users.list());
    } catch (err) {
      setError(err.message || "Could not load the user list.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    setMyName(currentUser?.full_name ?? "");
  }, [currentUser?.full_name]);

  const announce = (message) => {
    setSuccess(message);
    setError("");
    setTimeout(() => setSuccess(""), 5000);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setAdding(true);
    setError("");
    try {
      const created = await api.users.create({
        email: draft.email,
        full_name: draft.full_name,
        role: draft.role,
        ...(draft.method === "password"
          ? { password: draft.password }
          : { send_invitation: true }),
      });

      setDraft(blankDraft);
      setShowAdd(false);

      if (draft.method === "invite" && created.invitation && !created.invitation.sent) {
        // The account exists either way; only the email failed.
        setError(
          `${created.email} was added, but the invitation could not be sent: ${created.invitation.error}`
        );
      } else {
        announce(
          draft.method === "password"
            ? `${created.email} can sign in now with the password you set.`
            : `${created.email} was added and an invitation is on its way.`
        );
      }
      await load();
    } catch (err) {
      setError(err.message || "Could not add that account.");
    } finally {
      setAdding(false);
    }
  };

  const changeUser = async (id, patch, description) => {
    setBusyId(id);
    setError("");
    try {
      await api.users.update(id, patch);
      announce(description);
      if (id === currentUser?.id) await reload();
      await load();
      return true;
    } catch (err) {
      setError(err.message || "Could not update that account.");
      return false;
    } finally {
      setBusyId(null);
    }
  };

  /**
   * Delete an account for good.
   *
   * Disabling leaves someone on the list forever; after a few years that list
   * is mostly people who have left. What their deletion does not touch is what
   * they did - the activity log keeps their name and address as text written at
   * the time, so their history stays legible without their account existing.
   */
  const handleRemove = async (user) => {
    setBusyId(user.id);
    setError("");
    try {
      await api.users.remove(user.id);
      announce(`${user.email} has been deleted. Their past work keeps their name on it.`);
      setRemoving(null);
      await load();
    } catch (err) {
      setError(err.message || "Could not delete that account.");
      setRemoving(null);
    } finally {
      setBusyId(null);
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();

    const patch = {};
    const original = users.find((u) => u.id === edit.id);
    if (edit.full_name !== original.full_name) patch.full_name = edit.full_name;
    if (edit.email !== original.email) patch.email = edit.email;
    if (edit.role !== original.role) patch.role = edit.role;
    if (edit.password) patch.password = edit.password;

    if (Object.keys(patch).length === 0) {
      setEdit(null);
      return;
    }

    const changes = Object.keys(patch)
      .map((k) => (k === "password" ? "password" : k.replace("_", " ")))
      .join(", ");

    if (await changeUser(edit.id, patch, `Updated ${changes} for ${original.email}.`)) {
      setEdit(null);
    }
  };

  const handleResend = async (u) => {
    setBusyId(u.id);
    setError("");
    try {
      await api.users.sendInvitation(u.id);
      announce(`Invitation sent to ${u.email}.`);
    } catch (err) {
      setError(err.message || "Could not send the invitation.");
    } finally {
      setBusyId(null);
    }
  };

  const handleSaveName = async (e) => {
    e.preventDefault();
    setSavingName(true);
    setError("");
    try {
      await api.updateMe({ full_name: myName.trim() });
      // The greeting and the audit trail both read this, so refresh the
      // session copy rather than waiting for the next sign-in.
      await reload();
      announce("Your name has been updated.");
      await load();
    } catch (err) {
      setError(err.message || "Could not update your name.");
    } finally {
      setSavingName(false);
    }
  };

  return (
    <div className="card" style={{ marginBottom: "1.5rem" }}>
      <h2 className="card-title">
        <UserIcon size={20} />
        Users
      </h2>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {/* Your own name: the dashboard greets it and the activity log records it. */}
      <form onSubmit={handleSaveName} className="section-block">
        <label className="form-label">Your display name</label>
        <p className="muted">Shown on the dashboard and recorded against everything you do.</p>
        <div className="field-row">
          <input
            className="form-input"
            value={myName}
            onChange={(e) => setMyName(e.target.value)}
            placeholder="Your name"
          />
          <button
            type="submit"
            className="btn btn-outline"
            disabled={savingName || !myName.trim() || myName.trim() === currentUser?.full_name}
          >
            {savingName ? "Saving..." : "Save"}
          </button>
        </div>
      </form>

      {/* Add someone */}
      {!showAdd ? (
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
          <UserPlus size={16} />
          Add a user
        </button>
      ) : (
        <form onSubmit={handleAdd} className="user-add">
          <div className="user-grid">
            <div>
              <label className="form-label">Full name</label>
              <input
                className="form-input"
                value={draft.full_name}
                onChange={(e) => setDraft({ ...draft, full_name: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="form-label">Email</label>
              <input
                className="form-input"
                type="email"
                value={draft.email}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
                required
              />
            </div>
            <div>
              <label className="form-label">Role</label>
              <select
                className="form-select"
                value={draft.role}
                onChange={(e) => setDraft({ ...draft, role: e.target.value })}
              >
                <option value="operator">Operator</option>
                <option value="admin">Administrator</option>
              </select>
            </div>
          </div>

          <div style={{ marginTop: "1.25rem" }}>
            <label className="form-label">How do they get in?</label>
            <div className="radio-row">
              <label className="user-radio">
                <input
                  type="radio"
                  checked={draft.method === "password"}
                  onChange={() => setDraft({ ...draft, method: "password" })}
                />
                Set a password now — nothing is emailed
              </label>
              <label className="user-radio">
                <input
                  type="radio"
                  checked={draft.method === "invite"}
                  onChange={() => setDraft({ ...draft, method: "invite", password: "" })}
                />
                Email an invitation — they choose their own
              </label>
            </div>

            {draft.method === "password" && (
              <>
                <input
                  className="form-input"
                  type="text"
                  autoComplete="new-password"
                  placeholder={`At least ${MIN_PASSWORD} characters`}
                  value={draft.password}
                  onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                  minLength={MIN_PASSWORD}
                  required
                />
                <p className="muted" style={{ marginTop: "0.5rem" }}>
                  Shown as you type so you can read it out. They can change it later.
                </p>
              </>
            )}
          </div>

          <p className="muted" style={{ margin: "1.25rem 0" }}>
            Operators run mailings. Administrators additionally change Settings,
            refund labels and manage accounts.
          </p>

          <div className="button-row">
            <button type="submit" className="btn btn-primary" disabled={adding}>
              {adding ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
              {adding ? "Adding..." : "Add user"}
            </button>
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => {
                setShowAdd(false);
                setDraft(blankDraft);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Existing accounts */}
      {loading ? (
        <div className="muted" style={{ marginTop: "1rem" }}>
          <Loader2 size={16} className="spin" /> Loading accounts...
        </div>
      ) : (
        <ul className="plain-list">
          {users.map((u) => {
            const isSelf = u.id === currentUser?.id;
            const editing = edit?.id === u.id;

            if (editing) {
              return (
                <li key={u.id} className="list-row" style={{ display: "block" }}>
                  <form onSubmit={handleSaveEdit}>
                    <div className="user-grid">
                      <div>
                        <label className="form-label">Full name</label>
                        <input
                          className="form-input"
                          value={edit.full_name}
                          onChange={(e) => setEdit({ ...edit, full_name: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <label className="form-label">Email</label>
                        <input
                          className="form-input"
                          type="email"
                          value={edit.email}
                          onChange={(e) => setEdit({ ...edit, email: e.target.value })}
                          required
                        />
                      </div>
                      <div>
                        <label className="form-label">Role</label>
                        <select
                          className="form-select"
                          value={edit.role}
                          disabled={isSelf}
                          title={isSelf ? "You cannot change your own role" : ""}
                          onChange={(e) => setEdit({ ...edit, role: e.target.value })}
                        >
                          <option value="operator">Operator</option>
                          <option value="admin">Administrator</option>
                        </select>
                      </div>
                    </div>

                    <div style={{ marginTop: "1rem", maxWidth: "26rem" }}>
                      <label className="form-label">New password (optional)</label>
                      <input
                        className="form-input"
                        type="text"
                        autoComplete="new-password"
                        placeholder="Leave blank to keep the current one"
                        value={edit.password}
                        onChange={(e) => setEdit({ ...edit, password: e.target.value })}
                        minLength={MIN_PASSWORD}
                      />
                    </div>

                    <div className="button-row" style={{ marginTop: "1.25rem" }}>
                      <button type="submit" className="btn btn-sm btn-primary" disabled={busyId === u.id}>
                        <Check size={14} />
                        Save
                      </button>
                      <button type="button" className="btn btn-sm btn-outline" onClick={() => setEdit(null)}>
                        <X size={14} />
                        Cancel
                      </button>
                    </div>
                  </form>
                </li>
              );
            }

            return (
              <li key={u.id} className="list-row">
                <div style={{ minWidth: 0 }}>
                  <div className="list-row-title">
                    {u.full_name || u.email}
                    {isSelf && <span className="muted"> (you)</span>}
                    <span className={`badge ${u.role === "admin" ? "badge-amber" : "badge-green"}`} style={{ marginLeft: "0.5rem" }}>
                      {u.role === "admin" ? "Admin" : "Operator"}
                    </span>
                    {!u.active && (
                      <span className="badge badge-red" style={{ marginLeft: "0.375rem" }}>
                        Disabled
                      </span>
                    )}
                  </div>
                  <div className="list-row-sub">{u.email}</div>
                </div>

                <div className="button-row">
                  <button
                    className="btn btn-sm btn-outline"
                    disabled={busyId === u.id}
                    onClick={() =>
                      setEdit({
                        id: u.id,
                        full_name: u.full_name ?? "",
                        email: u.email,
                        role: u.role,
                        password: "",
                      })
                    }
                  >
                    <Pencil size={14} />
                    Edit
                  </button>

                  <button
                    className="btn btn-sm btn-outline"
                    disabled={busyId === u.id}
                    onClick={() => handleResend(u)}
                    title="Email them a link to set their own password"
                  >
                    <Mail size={14} />
                    Invite
                  </button>

                  {/* Refused for your own account by the API too - losing the
                      last administrator is the one mistake with no remedy. */}
                  <button
                    className="btn btn-sm btn-outline"
                    disabled={isSelf || busyId === u.id}
                    title={isSelf ? "You cannot disable your own account" : ""}
                    onClick={() =>
                      changeUser(
                        u.id,
                        { active: !u.active },
                        `${u.email} has been ${u.active ? "disabled" : "re-enabled"}.`
                      )
                    }
                  >
                    {u.active ? "Disable" : "Enable"}
                  </button>

                  <button
                    className="btn btn-sm btn-outline"
                    style={{ color: "#dc2626" }}
                    disabled={isSelf || busyId === u.id}
                    title={
                      isSelf
                        ? "You cannot delete your own account"
                        : "Remove this account entirely"
                    }
                    onClick={() => setRemoving(u)}
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {removing && (
        <div className="modal-backdrop" onClick={() => setRemoving(null)}>
          <div className="card modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>Delete this account?</h3>
                <p>
                  {removing.full_name || removing.email} ({removing.role})
                </p>
              </div>
            </div>
            <p className="muted">
              They will no longer be able to sign in, and the account is gone
              rather than switched off. Everything they did stays where it is —
              the activity log records their name and address against each entry,
              so their history remains readable afterwards.
            </p>
            <div className="modal-actions">
              <button className="btn btn-outline" onClick={() => setRemoving(null)}>
                Keep the account
              </button>
              <button
                className="btn btn-primary"
                style={{ background: "#dc2626" }}
                disabled={busyId === removing.id}
                onClick={() => handleRemove(removing)}
              >
                {busyId === removing.id ? (
                  <Loader2 size={14} className="spin" />
                ) : (
                  <Trash2 size={14} />
                )}
                Delete {removing.email}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
